/**
 * Progress display while the optimization workflow is running.
 * Shows the current status with a spinner and step descriptions.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

const DEFAULT_WORKFLOW_STEPS = [
  { key: 'pending', label: 'Starting workflow...' },
  { key: 'running_experiments', label: 'Running simulations...' },
  { key: 'scoring', label: 'Scoring conversations...' },
  { key: 'analyzing', label: 'Analyzing results...' },
  { key: 'optimizing', label: 'Generating prompt improvements...' },
  { key: 'awaiting_review', label: 'Ready for review' },
];

const CHANGE_GENERATION_STEPS = [
  { key: 'pending', label: 'Starting workflow...' },
  { key: 'generating_changes', label: 'Generating prompt improvements...' },
  { key: 'awaiting_review', label: 'Ready for review' },
];

const PREVIEW_SCORING_STEPS = [
  { key: 'pending', label: 'Preparing scored preview...' },
  { key: 'running_preview_experiments', label: 'Running simulations...' },
  { key: 'polling_preview_scores', label: 'Scoring conversations...' },
  { key: 'scores_ready', label: 'Scores ready' },
];

// Map backend statuses to step indices
const STATUS_TO_STEP: Record<string, number> = {
  pending: 0,
  selecting_datasets: 1,
  generating_dataset: 1,
  prepare_batch: 1,
  running_experiments: 1,
  polling_scores: 2,
  scoring: 2,
  optimizing: 3,
  optimization_complete: 4,
  awaiting_review: 5,
  applying_changes: 5,
  cancelled: -1,
  failed: -1,
};

function getStepIndex(status: string): number {
  return STATUS_TO_STEP[status] ?? 0;
}

function getProgressModel(status: string): {
  title: string;
  steps: { key: string; label: string }[];
  currentStep: number;
} {
  if (status === 'generating_changes') {
    return {
      title: 'Generating Prompt Changes',
      steps: CHANGE_GENERATION_STEPS,
      currentStep: 1,
    };
  }

  if (status === 'running_preview_experiments' || status === 'polling_preview_scores') {
    return {
      title: 'Scoring Updated Prompt',
      steps: PREVIEW_SCORING_STEPS,
      currentStep: status === 'running_preview_experiments' ? 1 : 2,
    };
  }

  return {
    title: 'Optimization in Progress',
    steps: DEFAULT_WORKFLOW_STEPS,
    currentStep: getStepIndex(status),
  };
}

const TERMINAL_ERROR_STATUSES = new Set(['cancelled', 'failed']);

interface Props {
  status: string;
}

const WorkflowProgress: React.FC<Props> = ({ status }) => {
  const isTerminalError = TERMINAL_ERROR_STATUSES.has(status);
  const { title, steps, currentStep } = getProgressModel(status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col gap-3 rounded-xl border border-border/40 bg-card p-5 max-w-md mx-auto"
    >
      <h3 className="text-sm font-medium text-heading">{title}</h3>
      <div className="space-y-2.5">
        {steps.map((step, idx) => {
          const isDone = isTerminalError ? false : idx < currentStep;
          const isCurrent = isTerminalError ? false : idx === currentStep;
          const isErrorStep = isTerminalError && idx === 0;

          return (
            <motion.div
              key={step.key}
              initial={isTerminalError || idx <= currentStep ? { opacity: 0, x: -8 } : {}}
              animate={isTerminalError || idx <= currentStep ? { opacity: 1, x: 0 } : { opacity: 0.3 }}
              transition={{ delay: idx * 0.05, duration: 0.25 }}
              className="flex items-center gap-2.5 text-sm"
            >
              {isErrorStep ? (
                <XCircle className="h-4 w-4 text-red-500 shrink-0" />
              ) : isDone ? (
                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              ) : isCurrent ? (
                <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />
              ) : (
                <div className="h-4 w-4 rounded-full border border-border/60 shrink-0" />
              )}
              <span
                className={
                  isErrorStep
                    ? 'text-red-600 dark:text-red-400 font-medium'
                    : isDone
                      ? 'text-muted-foreground'
                      : isCurrent
                        ? 'text-heading font-medium'
                        : 'text-muted-foreground/50'
                }
              >
                {isErrorStep
                  ? (status === 'cancelled' ? 'Workflow cancelled' : 'Workflow failed')
                  : step.label}
              </span>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default WorkflowProgress;
